import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import type { UserRepertoireData } from 'Types/RepertoireTypes';
import { isUserRepertoirePlayable } from '../../repertoireUploadState';

type SaveRepertoireDialogProps = {
  open: boolean;
  title: string;
  error: string;
  isSaving: boolean;
  onTitleChange: (title: string) => void;
  onClose: () => void;
  onSave: () => void;
};

export const SaveRepertoireDialog = ({
  open,
  title,
  error,
  isSaving,
  onTitleChange,
  onClose,
  onSave,
}: SaveRepertoireDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{
      sx: {
        backgroundColor: '#050B13',
        border: '1px solid rgba(121, 216, 255, 0.24)',
        borderRadius: '14px',
      },
    }}
  >
    <DialogTitle
      sx={{
        color: '#F3F7FF',
        fontFamily: 'Lato',
        fontSize: '18px',
        fontWeight: 800,
        padding: '20px 22px 14px',
      }}
    >
      Save Repertoire
    </DialogTitle>
    <DialogContent sx={{ padding: '22px 22px 4px', overflow: 'visible' }}>
      <TextField
        autoFocus
        fullWidth
        value={title}
        label="Title"
        disabled={isSaving}
        onChange={(event) => onTitleChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onSave();
          }
        }}
        sx={saveTitleInputStyles}
      />
      {error && (
        <Box role="alert" sx={saveErrorStyles}>
          {error}
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ padding: '18px 22px 22px', gap: '10px' }}>
      <Button
        variant="outlined"
        onClick={onClose}
        disabled={isSaving}
        sx={saveDialogCancelButtonStyles}
      >
        Cancel
      </Button>
      <Button variant="contained" onClick={onSave} disabled={isSaving} sx={saveDraftButtonStyles}>
        {isSaving && <CircularProgress size={16} sx={{ color: '#FFFFFF', marginRight: '8px' }} />}
        {isSaving ? 'Saving...' : 'Save'}
      </Button>
    </DialogActions>
  </Dialog>
);

type SaveSuccessDialogProps = {
  repertoire: UserRepertoireData | null;
  onClose: () => void;
  onPractice: () => void;
};

export const SaveSuccessDialog = ({ repertoire, onClose, onPractice }: SaveSuccessDialogProps) => {
  const canPractice = repertoire ? isUserRepertoirePlayable(repertoire) : false;

  return (
    <Dialog
      open={Boolean(repertoire)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#050B13',
          border: '1px solid rgba(70, 159, 106, 0.38)',
          borderRadius: '14px',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: '#F3F7FF',
          fontFamily: 'Lato',
          fontSize: '18px',
          fontWeight: 800,
          padding: '20px 22px 8px',
        }}
      >
        Repertoire Saved
      </DialogTitle>
      <DialogContent sx={{ padding: '10px 22px 4px' }}>
        <Box sx={{ color: 'rgba(226, 234, 255, 0.76)', fontSize: '14px', lineHeight: 1.55 }}>
          {repertoire?.name}
        </Box>
        <Box role="status" aria-live="polite" sx={savedStatusPillStyles(canPractice)}>
          {canPractice ? 'Ready to practice' : 'Processing score'}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '18px 22px 22px', gap: '10px' }}>
        <Button variant="outlined" onClick={onClose} sx={saveDialogCancelButtonStyles}>
          Keep Editing
        </Button>
        <Button
          variant="contained"
          onClick={onPractice}
          disabled={!canPractice}
          sx={saveDraftButtonStyles}
        >
          Practice
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type ClearDraftDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const ClearDraftDialog = ({ open, onClose, onConfirm }: ClearDraftDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{
      sx: {
        backgroundColor: '#050B13',
        border: '1px solid rgba(255, 157, 157, 0.26)',
        borderRadius: '14px',
      },
    }}
  >
    <DialogTitle
      sx={{
        color: '#F3F7FF',
        fontFamily: 'Lato',
        fontSize: '18px',
        fontWeight: 800,
        padding: '20px 22px 8px',
      }}
    >
      Clear Draft?
    </DialogTitle>
    <DialogContent sx={{ padding: '10px 22px 4px' }}>
      <Box sx={{ color: 'rgba(226, 234, 255, 0.76)', fontSize: '14px', lineHeight: 1.55 }}>
        This will remove all notes, pedal events, and draft history. This action cannot be undone.
      </Box>
    </DialogContent>
    <DialogActions sx={{ padding: '18px 22px 22px', gap: '10px' }}>
      <Button variant="outlined" onClick={onClose} sx={saveDialogCancelButtonStyles}>
        Cancel
      </Button>
      <Button variant="contained" onClick={onConfirm} sx={clearDraftButtonStyles}>
        Clear
      </Button>
    </DialogActions>
  </Dialog>
);

const saveDraftButtonStyles = {
  background: 'linear-gradient(90deg, #469F6A 0%, #5FBF84 100%)',
  color: '#FFFFFF',
  borderRadius: '999px',
  fontWeight: 800,
  minHeight: '36px',
  paddingX: '18px',
  textTransform: 'none',
  boxShadow: '0 8px 18px rgba(70, 159, 106, 0.24)',
  '&:hover': {
    background: 'linear-gradient(90deg, #56AD78 0%, #6ACB91 100%)',
  },
  '&.Mui-disabled': {
    background: 'rgba(118, 124, 140, 0.42)',
    color: 'rgba(255, 255, 255, 0.58)',
    boxShadow: 'none',
  },
};

const saveDialogCancelButtonStyles = {
  color: '#EAF2FF',
  borderColor: 'rgba(255,255,255,0.16)',
  borderRadius: '999px',
  paddingX: '18px',
  textTransform: 'none',
  '&:hover': {
    borderColor: 'rgba(121, 216, 255, 0.32)',
    backgroundColor: 'rgba(121, 216, 255, 0.08)',
  },
};

const clearDraftButtonStyles = {
  background: 'linear-gradient(90deg, #B94C4C 0%, #D46363 100%)',
  color: '#FFFFFF',
  borderRadius: '999px',
  fontWeight: 800,
  minHeight: '36px',
  paddingX: '18px',
  textTransform: 'none',
  boxShadow: '0 8px 18px rgba(185, 76, 76, 0.22)',
  '&:hover': {
    background: 'linear-gradient(90deg, #C65A5A 0%, #E07171 100%)',
  },
};

const saveTitleInputStyles = {
  marginTop: '2px',
  '& .MuiInputLabel-root': {
    color: 'rgba(226, 234, 255, 0.58)',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#79D8FF',
  },
  '& .MuiOutlinedInput-root': {
    color: '#F3F7FF',
    borderRadius: '10px',
    '& fieldset': {
      borderColor: 'rgba(226, 234, 255, 0.18)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(121, 216, 255, 0.32)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#79D8FF',
    },
  },
};

const saveErrorStyles = {
  color: '#FF9A9A',
  fontSize: '13px',
  marginTop: '10px',
};

const savedStatusPillStyles = (ready: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '999px',
  padding: '6px 10px',
  marginTop: '14px',
  fontSize: '12px',
  fontWeight: 800,
  color: ready ? '#DFFFE9' : '#FFE7BC',
  backgroundColor: ready ? 'rgba(70, 159, 106, 0.18)' : 'rgba(255, 185, 94, 0.16)',
});
